import { Alert, Grid, Typography } from '@mui/material'

import {
  COLOR_ALLOCATION,
  COLOR_FIRST_BID_ANALYSIS,
  COLOR_FIRST_BID_TO_ALLOCATION,
  COLOR_LOWEST_BID,
} from 'components/reports/Metrics/constants'
import {
  useGetBidAnalysisBidsPerJobQuery,
  useGetBidAnalysisFirstBidToAllocationQuery,
  useGetBidAnalysisTimeToAllocationQuery,
  useGetBidAnalysisTimeToFirstBidQuery,
  useGetBidAnalysisTimeToLowestBidQuery,
} from 'generated/graphql'

import { BidAnalysisHistoryNote } from './BidAnalysisHistoryNote'
import { BidsPerJobCard } from './BidsPerJobCard'
import { TimeToHistogramCard } from './TimeToHistogramCard'
import { useBidAnalysisQueryInput } from './useBidAnalysisQueryInput'

export const BidAnalysisCards = () => {
  const { start, hasValidRange, isCustom, input } = useBidAnalysisQueryInput()

  const queryOptions = {
    variables: { input },
    skip: !hasValidRange,
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-and-network' as const,
  }

  const bidsPerJob = useGetBidAnalysisBidsPerJobQuery(queryOptions)
  const firstBid = useGetBidAnalysisTimeToFirstBidQuery(queryOptions)
  const allocation = useGetBidAnalysisTimeToAllocationQuery(queryOptions)
  const firstBidToAllocation = useGetBidAnalysisFirstBidToAllocationQuery(queryOptions)
  const lowestBid = useGetBidAnalysisTimeToLowestBidQuery(queryOptions)

  const bidsPerJobData =
    bidsPerJob.data?.bidAnalysisBidsPerJob ?? bidsPerJob.previousData?.bidAnalysisBidsPerJob
  const firstBidData =
    firstBid.data?.bidAnalysisTimeToFirstBid ?? firstBid.previousData?.bidAnalysisTimeToFirstBid
  const allocationData =
    allocation.data?.bidAnalysisTimeToAllocation ??
    allocation.previousData?.bidAnalysisTimeToAllocation
  const firstBidToAllocationData =
    firstBidToAllocation.data?.bidAnalysisFirstBidToAllocation ??
    firstBidToAllocation.previousData?.bidAnalysisFirstBidToAllocation
  const lowestBidData =
    lowestBid.data?.bidAnalysisTimeToLowestBid ?? lowestBid.previousData?.bidAnalysisTimeToLowestBid

  if (!hasValidRange) {
    return (
      <Alert severity='info'>
        {isCustom
          ? 'Choose a From and To date to see the metrics.'
          : 'Choose a period to see the metrics.'}
      </Alert>
    )
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BidAnalysisHistoryNote start={start} />
      </Grid>

      <Grid item xs={12}>
        <BidsPerJobCard data={bidsPerJobData} loading={bidsPerJob.loading && !bidsPerJobData} />
      </Grid>

      <Grid item xs={12}>
        <Typography variant='h6' fontWeight={700} sx={{ color: 'primary.main' }}>
          Time-to Metrics
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Distribution of time taken at key stages in the job lifecycle.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TimeToHistogramCard
          title='Time from Job Posting'
          info='Offers from both networks are timed: OnePoint bids placed in the Driver App and Courier Exchange quotes, whichever arrived first. First Bid counts only jobs that received an offer; Allocation counts only jobs where the supplier whose offer won was the one allocated. The two therefore cover different jobs and their medians are not directly comparable. Jobs posted very recently may not be allocated yet, so the Allocation figures always trail slightly.'
          footnote='Times are calculated from when the job was posted to the network.'
          loading={(firstBid.loading && !firstBidData) || (allocation.loading && !allocationData)}
          series={[
            { label: '→ First Bid', color: COLOR_FIRST_BID_ANALYSIS, histogram: firstBidData },
            { label: '→ Allocation', color: COLOR_ALLOCATION, histogram: allocationData },
          ]}
        />
      </Grid>

      <Grid item xs={12}>
        <TimeToHistogramCard
          title='Time from First Bid to Allocation'
          info='How long bidding stayed open once suppliers began responding. Offers from both networks are timed: OnePoint bids placed in the Driver App and Courier Exchange quotes. A job needs an offer, and an allocation to the supplier whose offer won, to appear here. Where an operator allocated a job before any offer arrived the gap runs backwards, so that job is left out rather than shown as a negative time.'
          footnote='Times are calculated from when the first bid was received.'
          loading={firstBidToAllocation.loading && !firstBidToAllocationData}
          series={[
            {
              label: 'First bid to allocation',
              color: COLOR_FIRST_BID_TO_ALLOCATION,
              histogram: firstBidToAllocationData,
            },
          ]}
        />
      </Grid>

      <Grid item xs={12}>
        <TimeToHistogramCard
          title='Time from Job Posting to Lowest Bid'
          info='When the most competitive price typically arrives. Offers from both networks are timed: OnePoint bids placed in the Driver App and Courier Exchange quotes, whichever is cheapest. Where two share the lowest price the earlier one is used. If a supplier lowers their price later, the time shown is when they first bid, not when they dropped it. Where a job attracted priced offers from both networks they are converted to GBP before being compared, and a job carrying an offer in a currency we hold no rate for is left out of this chart.'
          footnote='Based on the cheapest offer received for each job, from either network.'
          averageCaption='Mean'
          loading={lowestBid.loading && !lowestBidData}
          series={[
            { label: 'Time to lowest bid', color: COLOR_LOWEST_BID, histogram: lowestBidData },
          ]}
        />
      </Grid>
    </Grid>
  )
}
